import React from 'react';
import { Link } from 'react-router-dom';
import { useBranding } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Shield, Lock, Users, FileText, Activity, Fingerprint, Key, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  const { appName, logoUrl } = useBranding();
  const { t } = useLanguage();
  
  const features = [
    {
      icon: <Lock className="w-6 h-6" />,
      title: t('features.encryption.title'),
      description: t('features.encryption.description')
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: t('features.roles.title'),
      description: t('features.roles.description')
    },
    {
      icon: <Activity className="w-6 h-6" />,
      title: t('features.audit.title'),
      description: t('features.audit.description')
    },
    {
      icon: <Fingerprint className="w-6 h-6" />,
      title: t('features.auth.title'),
      description: t('features.auth.description')
    },
    {
      icon: <Key className="w-6 h-6" />,
      title: t('features.keys.title'),
      description: t('features.keys.description')
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: t('features.docs.title'),
      description: t('features.docs.description')
    }
  ];

  const trustPoints = t('landing.trustPoints');

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzZXJ2ZXIlMjByb29tJTIwZGFyayUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzM1MTgyOTF8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/90 to-[#050505]" />
        
        {/* Navigation */}
        <nav className="relative z-10 px-6 md:px-12 lg:px-24 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt={appName} className="h-8 w-auto" />
              ) : (
                <Shield className="w-8 h-8 text-primary" />
              )}
              <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {appName}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-white/5 font-mono uppercase tracking-wider text-sm" data-testid="nav-login-btn">
                  {t('nav.login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider text-sm btn-glow" data-testid="nav-register-btn">
                  {t('nav.register')}
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 px-6 md:px-12 lg:px-24 pt-20 pb-32">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-mono uppercase tracking-widest mb-6">
              <Lock className="w-3 h-3" />
              <span>{t('landing.badge')}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.headline')}<br />
              <span className="text-primary">{t('landing.headlineHighlight')}</span>
            </h1>
            <p className="text-lg text-white/70 max-w-2xl mb-8 leading-relaxed">
              {t('landing.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider btn-glow h-12 px-8" data-testid="hero-get-started-btn">
                  {t('landing.ctaPrimary')}
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 font-mono uppercase tracking-wider h-12 px-8" data-testid="hero-login-btn">
                  {t('landing.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-[#0A0A0A]">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t('landing.featuresTitle')} <span className="text-primary">{t('landing.featuresHighlight')}</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            {t('landing.featuresDescription')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm p-6 hover:border-primary/50 transition-all duration-300 card-hover"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:bg-primary/20 transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {feature.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Security Badge Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-semibold tracking-tight text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('landing.whyTrustTitle')} <span className="text-primary">{appName}</span>?
            </h2>
            <div className="space-y-4">
              {(Array.isArray(trustPoints) ? trustPoints : []).map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxwYWRsb2NrfGVufDB8fHx8MTc3MzUxODMyOXww&ixlib=rb-4.1.0&q=85"
              alt="Security Concept"
              className="rounded-sm border border-white/10"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-[#0A0A0A]">
        <div className="text-center max-w-2xl mx-auto">
          {logoUrl ? (
            <img src={logoUrl} alt={appName} className="h-16 mx-auto mb-6" />
          ) : (
            <Shield className="w-16 h-16 text-primary mx-auto mb-6 animate-glow-pulse" />
          )}
          <h2 className="text-4xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-white/60 mb-8">
            {t('landing.ctaDescription')}
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider btn-glow h-12 px-8" data-testid="cta-login-btn">
              {t('landing.ctaButton')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-24 py-12 border-t border-white/10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt={appName} className="h-6 w-auto" />
            ) : (
              <Shield className="w-6 h-6 text-primary" />
            )}
            <span className="font-bold text-lg text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {appName}
            </span>
          </div>
          <p className="text-white/40 text-sm">
            © 2024 {appName}. {t('landing.footer')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
